import { useState, useEffect, useRef, useCallback } from 'react';

export const useStockfish = () => {
    const [isReady, setIsReady] = useState(false);
    const [evaluation, setEvaluation] = useState(null);
    const [bestMove, setBestMove] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const stockfishRef = useRef(null);
    const analysisTimeoutRef = useRef(null);
    const currentFenRef = useRef(null);

    useEffect(() => {
        console.log('Initializing Stockfish engine (multi-threaded)...');

        // Use multi-threaded version (requires COOP/COEP headers)
        const stockfish = new Worker('/stockfish/stockfish-nnue-16.js');
        stockfishRef.current = stockfish;

        // Handle messages from Stockfish
        stockfish.onmessage = (event) => {
            const message = event.data;
            console.log('Stockfish:', message);

            // Engine is ready
            if (message === 'uciok') {
                setIsReady(true);
                console.log('Stockfish ready (multi-threaded)!');
            }

            // Analysis output
            if (message.startsWith('info')) {
                parseStockfishInfo(message);
            }

            // Best move found
            if (message.startsWith('bestmove')) {
                const moveMatch = message.match(/bestmove (\w+)/);
                if (moveMatch) {
                    setBestMove(moveMatch[1]);
                    setIsAnalyzing(false);
                }
            }
        };

        // Initialize UCI protocol
        stockfish.postMessage('uci');
        stockfish.postMessage('setoption name UCI_AnalyseMode value true');
        stockfish.postMessage('setoption name MultiPV value 1');
        stockfish.postMessage('setoption name Threads value 4'); // Use 4 threads
        stockfish.postMessage('isready');

        return () => {
            console.log('Cleanup Stockfish');
            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }
            stockfish.postMessage('quit');
            stockfish.terminate();
        };
    }, []);

    const parseStockfishInfo = useCallback((message) => {
        // Parse evaluation from info string
        // Example: info depth 20 seldepth 25 score cp 25 nodes 100000 nps 50000 time 2000 pv e2e4

        const depthMatch = message.match(/depth (\d+)/);
        const scoreMatch = message.match(/score (cp|mate) (-?\d+)/);
        const pvMatch = message.match(/pv (\S+)/);

        if (scoreMatch && depthMatch && currentFenRef.current) {
            const depth = parseInt(depthMatch[1]);
            const scoreType = scoreMatch[1];
            let scoreValue = parseInt(scoreMatch[2]);

            // CRITICAL FIX: Stockfish evaluates from SIDE TO MOVE perspective in UCI mode
            // We need to ALWAYS convert to White's perspective
            const fenParts = currentFenRef.current.split(' ');
            const sideToMove = fenParts[1]; // 'w' or 'b'

            // If it's Black to move, Stockfish gives evaluation from Black's perspective
            // We need to flip the sign to convert to White's perspective
            if (sideToMove === 'b') {
                scoreValue = -scoreValue;
            }

            let evaluation;

            if (scoreType === 'mate') {
                // Mate in N moves
                evaluation = {
                    type: 'mate',
                    value: scoreValue,
                    depth: depth,
                    displayValue: `M${Math.abs(scoreValue)}`
                };
            } else {
                // Centipawn evaluation (divide by 100 to get pawns)
                const pawns = scoreValue / 100;
                evaluation = {
                    type: 'cp',
                    value: pawns,
                    depth: depth,
                    displayValue: Math.abs(pawns).toFixed(2)
                };
            }

            console.log(`Position: ${currentFenRef.current}`);
            console.log(`Side to move: ${sideToMove}`);
            console.log(`Raw Stockfish score: ${parseInt(scoreMatch[2])} (type: ${scoreType})`);
            console.log(`Adjusted score (White's POV): ${scoreValue}`);
            console.log('Final evaluation:', evaluation);

            setEvaluation(evaluation);

            if (pvMatch) {
                setBestMove(pvMatch[1]);
            }
        }
    }, []);

    const analyzePosition = useCallback((fen, depth = 22) => {
        if (!isReady || !stockfishRef.current) {
            console.warn('Stockfish not ready for analysis');
            return;
        }

        console.log('Analyzing position:', fen);
        currentFenRef.current = fen;
        setIsAnalyzing(true);
        setBestMove(null);
        setEvaluation(null);

        const stockfish = stockfishRef.current;

        // Stop any previous analysis
        stockfish.postMessage('stop');

        // Clear previous timeout
        if (analysisTimeoutRef.current) {
            clearTimeout(analysisTimeoutRef.current);
        }

        // Small delay to ensure stop command is processed
        analysisTimeoutRef.current = setTimeout(() => {
            // Set up new position
            stockfish.postMessage('ucinewgame');
            stockfish.postMessage(`position fen ${fen}`);
            stockfish.postMessage(`go depth ${depth}`);
        }, 50);
    }, [isReady]);

    const stopAnalysis = useCallback(() => {
        if (stockfishRef.current) {
            console.log('Stopping analysis...');
            stockfishRef.current.postMessage('stop');
            setIsAnalyzing(false);
        }
    }, []);

    return {
        isReady,
        evaluation,
        bestMove,
        isAnalyzing,
        analyzePosition,
        stopAnalysis
    };
};